import { useEffect, useState } from 'react';
import type { DeskWatchState, WatchSourceType } from '@tsr/shared-types';
import { fetchDeskWatch, setDeskWatch } from '../api';
import { getSessionId } from '../lib/operatorActions';
import { showToast } from '../lib/toast';
import {
  STOP_WATCH_ACTION_LABEL,
  WATCH_ACTION_LABEL,
  WATCHING_STATE_LABEL,
  deskWatchCountLabel,
  watchToggleTitle,
} from '../deskWatchVocabulary';

/**
 * W4c DUTY WATCH (council A.3 / #526) — the per-DESK "Watching / On watch" toggle + count badge
 * on a file surface (incident / assistance request / agency task). A desk marks "eyes on this
 * file" WITHOUT owning it — NOT command, NOT ownership, NOT the Situation Unit function.
 *
 * NON-EXCLUSIVE + ADDITIVE: toggling this desk's watch NEVER evicts another desk's watch; the
 * "N desks watching" badge is the additive count. The cross-tenant count is DORMANT until #190
 * (council A.8) — in the single-seat demo it reads 0/1 (a NAMED SCOPE-OUT).
 *
 * Two chrome variants for the two host surfaces (same semantics, same vocabulary):
 *   - 'pill' — the incident panel header, visually PARALLEL to the owned Pill (subtle fill).
 *   - 'chip' — the assistance-request header, MIRRORING the "✓ Received" bordered chip.
 */
export function DeskWatchControl({
  sourceType,
  sourceId,
  variant = 'chip',
}: {
  sourceType: WatchSourceType;
  sourceId: string;
  variant?: 'pill' | 'chip';
}): JSX.Element | null {
  const [state, setState] = useState<DeskWatchState | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchDeskWatch(sourceType, sourceId, ctl.signal)
      .then((s) => setState(s))
      .catch(() => {
        /* best-effort — the control simply hides until the state loads */
      });
    return () => ctl.abort();
  }, [sourceType, sourceId]);

  if (!state) return null;

  const watching = state.watching;
  const countLabel = deskWatchCountLabel(state.count);

  const toggle = (): void => {
    if (busy) return;
    setBusy(true);
    void setDeskWatch(sourceType, sourceId, !watching, getSessionId())
      .then((s) => {
        setState(s);
        showToast(s.watching ? `${WATCHING_STATE_LABEL} — eyes on this file.` : 'Stopped watching this file.', {
          variant: 'info',
        });
      })
      .catch(() => showToast('Could not update the watch.', { variant: 'error' }))
      .finally(() => setBusy(false));
  };

  // ── 'chip' — mirrors the "✓ Received" bordered chip (assistance-request header). ──────────
  if (variant === 'chip') {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="desk-watch-control">
        <button
          type="button"
          data-testid="desk-watch-toggle"
          aria-pressed={watching}
          aria-label={watching ? STOP_WATCH_ACTION_LABEL : WATCH_ACTION_LABEL}
          onClick={toggle}
          disabled={busy}
          title={watchToggleTitle(watching, sourceType)}
          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border disabled:opacity-50 ${
            watching
              ? 'border-sky-500/60 text-sky-300'
              : 'border-slate-600 text-slate-300 hover:border-sky-500/60 hover:text-sky-300'
          }`}
        >
          <span aria-hidden>{'\u{1F441}'}</span>
          {watching ? WATCHING_STATE_LABEL : WATCH_ACTION_LABEL}
        </button>
        {countLabel && (
          <span
            data-testid="desk-watch-count"
            title={countLabel}
            className="text-[10px] uppercase tracking-wide text-slate-400"
          >
            {countLabel}
          </span>
        )}
      </span>
    );
  }

  // ── 'pill' — visually parallel to the incident owned Pill (subtle fill, no border). ───────
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="desk-watch-control">
      <button
        type="button"
        data-testid="desk-watch-toggle"
        aria-pressed={watching}
        onClick={toggle}
        disabled={busy}
        title={watchToggleTitle(watching, sourceType)}
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium disabled:opacity-50 ${
          watching
            ? 'bg-sky-500/20 text-sky-300'
            : 'bg-slate-700/40 text-slate-300 hover:bg-sky-500/20 hover:text-sky-300'
        }`}
      >
        <span aria-hidden>{'\u{1F441}'}</span>
        {watching ? WATCHING_STATE_LABEL : WATCH_ACTION_LABEL}
      </button>
      {countLabel && (
        <span data-testid="desk-watch-count" title={countLabel} className="text-[10px] text-slate-400">
          {countLabel}
        </span>
      )}
    </span>
  );
}
